import * as Sentry from '@sentry/react';

import { Environment } from '../Environment';

import type { IMonitoringConfig, ISentryConfig } from './types';

function getTracesSampleRate(environment: Environment): number {
  // Only report a fraction of production transaction traces to Sentry itself.
  //
  // Sentry has a quota of "performance units" for measuring FE performance. We
  // don't really use this feature at present, but it's nice to have the data.
  // When tracing at 20%, we use a bit more than 100k units a month (& get warned at 80k).
  // We don't frequently use Sentry for analyzing performance, but for the rare cases we
  // do, it's only necessary to collect a small sample of data.
  const prodTracesSampleRate = 0.1;

  const nonProdTracesSampleRate = 1.0;

  return environment === Environment.PRODUCTION ? prodTracesSampleRate : nonProdTracesSampleRate;
}

const COLOR_ERROR_ID_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
/**
 * Sentry does generate a unique id, but it is too long for users to relay back to us.
 * This generates a simple text string.
 * @returns a reasonably unique and human readable string.
 */
export const getRandomErrorId = (): string => {
  let errorId = '';
  for (let i = 0; i < 6; i += 1) {
    if (i !== 0 && i % 2 === 0) {
      errorId += '-';
    }
    errorId +=
      COLOR_ERROR_ID_CHARACTERS[Math.floor(Math.random() * COLOR_ERROR_ID_CHARACTERS.length)];
  }
  return errorId;
};

let latestColorErrorId = '';
export function getLatestSentryEventId(): string | undefined {
  return latestColorErrorId;
}

function initSentry(environment: Environment, config: ISentryConfig): void {
  const { dsn } = config;
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn('Unable to determine Sentry DSN');
    return;
  }

  const sentryOptions: Sentry.BrowserOptions = {
    ...config,
    environment, // `local`, `staging`, `testing`, `production`, or `unknown`
    integrations: [Sentry.browserTracingIntegration()],
    denyUrls: [/cdn\.cookielaw\.org/i], // Removed cookie consent dialog on the sites, but leaving this in case we add it back
    tracesSampleRate: getTracesSampleRate(environment),
    beforeSend: (event) => {
      const colorErrorId = getRandomErrorId();
      // Sentry seems to be providing an entirely different `eventId` to the ErrorBoundary Fallback than `event.event_id`
      // Unfortunately, there doesn't seem to be a reliable value that we can use.
      // Instead, capture the latest generated error id for use in the UI.
      latestColorErrorId = colorErrorId;
      return {
        ...event,
        tags: {
          ...event.tags,
          colorErrorId,
        },
      };
    },
  };

  Sentry.init(sentryOptions);
}

let monitoringIsInitialized = false;
/**
 * This function is only used for testing
 */
// eslint-disable-next-line no-underscore-dangle
export function _resetMonitoringIsInitialized(): void {
  monitoringIsInitialized = false;
}

export function initMonitoring(monitoringConfig: IMonitoringConfig): void {
  if (monitoringIsInitialized) {
    return;
  }
  monitoringIsInitialized = true;
  const { environment, sentryConfig } = monitoringConfig;

  initSentry(environment, sentryConfig);
}

export function getInstrumentedApp(App: React.FC, monitoringConfig: IMonitoringConfig): React.FC {
  initMonitoring(monitoringConfig);
  return Sentry.withProfiler(App);
}
